import L from "leaflet";
import "leaflet.markercluster";

const DEFAULT_CENTER = [51.1657, 10.4515];
const DEFAULT_ZOOM = 6;
const MOBILE_QUERY = "(max-width: 767px)";
const MIN_ZOOM_OFFSET = 0.01;
const SNAP_OPTIONS = {
    animate: true,
    duration: 0.18,
    easeLinearity: 1,
};
const MAX_BOUNDS_PADDING = 0.25;
const MOBILE_MAX_BOUNDS_PADDING = 0.35;
const POPUP_MAX_BOUNDS_PADDING = 0.75;
const MOBILE_POPUP_MAX_BOUNDS_PADDING = 1;
const REGION_MAX_RING_POINTS = 700;
const REGION_SMOOTH_FACTOR = 0;
const MAP_JSON_RETRY_DELAYS = [0, 200, 800, 2000, 5000];
const MAP_JSON_BACKGROUND_RETRY_DELAY = 5000;
const MARKER_TYPE_COLORS = new Map([
    ["bildungsnetzwerk", "#4D7EB2"],
    ["branchenverband", "#64748B"],
    ["digitalnetzwerk", "#0B407D"],
    ["exportnetzwerk", "#3B82F6"],
    ["forschungsnetzwerk", "#7C3AED"],
    ["frauennetzwerk", "#DB2777"],
    ["gesundheitsnetzwerk", "#059669"],
    ["grundernetzwerk", "#F97316"],
    ["handwerksnetzwerk", "#92400E"],
    ["innovationsnetzwerk", "#0891B2"],
    ["kreativnetzwerk", "#D946EF"],
    ["nachhaltigkeitsnetzwerk", "#16A34A"],
    ["technologienetzwerk", "#2563EB"],
    ["unternehmensnetzwerk", "#0B407D"],
    ["wirtschaftsforderung", "#CA8A04"],
]);
const MARKER_FALLBACK_COLORS = [
    "#0B407D",
    "#16A34A",
    "#F97316",
    "#7C3AED",
    "#0891B2",
    "#DB2777",
    "#CA8A04",
    "#64748B",
];
const EMAIL_OBFUSCATION_LABEL = "@";

class LocationMap {
    constructor(element) {
        this.element = element;
        this.regionsUrl = element.dataset.locationMapRegionsUrl || "";
        this.citiesUrl = element.dataset.locationMapCitiesUrl || "";
        this.body = element.querySelector("[data-location-map-view]");
        this.canvas = element.querySelector("[data-location-map-canvas]");
        this.filter = element.querySelector("[data-location-map-filter]");
        this.list = element.querySelector("[data-location-map-list]");
        this.count = element.querySelector("[data-location-map-count]");
        this.empty = element.querySelector("[data-location-map-empty]");
        this.switchButtons = [
            ...element.querySelectorAll("[data-location-map-switch]"),
        ];
        this.data = this.readData();
        this.entries = this.data.entries.filter((entry) =>
            this.hasCoordinates(entry),
        );
        this.markers = new Map();
        this.activeState = "";
        this.pendingPopupEntryId = null;
        this.popupRequestId = 0;
        this.regionBounds = null;
        this.stateBounds = new Map();
        this.regionMinZoom = null;
        this.isPopupBoundsActive = false;
        this.filteredEntries = [];
        this.geoJsonLoadPromise = null;
        this.geoJsonRetryTimer = null;
        this.isBaseMapReady = false;
        this.areCitiesReady = false;
        this.visibilityObserver = null;
        this.markerTabOrderFrame = null;

        if (!this.body || !this.canvas || !this.filter || !this.list) {
            return;
        }

        this.initMap();
        this.bindEvents();
        this.applyFilter();
    }

    readData() {
        const script = this.element.querySelector("[data-location-map-data]");
        if (!script?.textContent?.trim()) {
            return { entries: [], states: [] };
        }

        try {
            const data = JSON.parse(script.textContent);
            return {
                entries: Array.isArray(data.entries) ? data.entries : [],
                states: Array.isArray(data.states) ? data.states : [],
            };
        } catch {
            return { entries: [], states: [] };
        }
    }

    initMap() {
        this.map = L.map(this.canvas, {
            maxZoom: 19,
            maxBoundsViscosity: 0,
            minZoom: 5,
            preferCanvas: false,
            scrollWheelZoom: true,
        }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
        this.regionLayer = L.geoJSON(null, {
            className: "bundesland",
            interactive: true,
            renderer: L.svg(),
            smoothFactor: REGION_SMOOTH_FACTOR,
            style: () => ({
                color: "#fff",
                fillColor: "#d8d8d8",
                fillOpacity: 1,
                opacity: 0.8,
                weight: 1,
            }),
        }).addTo(this.map);
        this.cityLayer = L.layerGroup().addTo(this.map);

        this.clusterLayer = L.markerClusterGroup({
            showCoverageOnHover: false,
            iconCreateFunction: (cluster) =>
                L.divIcon({
                    html: `<span>${cluster.getChildCount()}</span>`,
                    className: "location-map__cluster",
                    iconSize: L.point(44, 44),
                }),
        });

        this.map.addLayer(this.clusterLayer);
        this.map.on("dragend zoomend", () => {
            this.snapMapToRegionBounds();
            this.scheduleMarkerTabOrderUpdate();
        });
        this.map.on("moveend", () => this.scheduleMarkerTabOrderUpdate());
        this.clusterLayer.on("animationend", () =>
            this.scheduleMarkerTabOrderUpdate(),
        );
        this.entries.forEach((entry) => this.createMarker(entry));
        this.loadGeoJsonMap();
    }

    loadGeoJsonMap() {
        if (this.isBaseMapReady && this.areCitiesReady) {
            return Promise.resolve(true);
        }

        if (this.geoJsonLoadPromise) {
            return this.geoJsonLoadPromise;
        }

        this.clearGeoJsonRetry();
        if (this.isBaseMapReady) {
            this.element.dataset.locationMapRegionsState = "ready";
            this.element.dataset.locationMapCitiesState = "loading";
            this.geoJsonLoadPromise = this.loadCitiesAfterRegions().finally(
                () => {
                    this.geoJsonLoadPromise = null;
                },
            );

            return this.geoJsonLoadPromise;
        }

        this.element.dataset.locationMapRegionsState = "loading";
        this.element.dataset.locationMapCitiesState = this.areCitiesReady
            ? "ready"
            : "loading";

        this.geoJsonLoadPromise = this.loadJsonWithRetry(this.regionsUrl)
            .then((regions) => this.renderRequiredRegions(regions))
            .then(() => this.loadCitiesAfterRegions())
            .catch(() => {
                this.element.dataset.locationMapRegionsState = "error";
                this.element.dataset.locationMapCitiesState = this.areCitiesReady
                    ? "ready"
                    : "waiting";
                this.scheduleGeoJsonRetry();
                return false;
            })
            .finally(() => {
                this.geoJsonLoadPromise = null;
            });

        return this.geoJsonLoadPromise;
    }

    renderRequiredRegions(regions) {
        if (!this.renderRegions(regions)) {
            throw new Error("Invalid regions GeoJSON");
        }

        this.element.dataset.locationMapRegionsState = "ready";

        return true;
    }

    loadCitiesAfterRegions() {
        if (!this.citiesUrl) {
            this.areCitiesReady = true;
            this.element.dataset.locationMapCitiesState = "ready";

            return Promise.resolve(true);
        }

        return this.loadJsonWithRetry(this.citiesUrl)
            .then((cities) => {
                this.renderCities(cities);
                this.areCitiesReady = true;
                this.element.dataset.locationMapCitiesState = "ready";
                return true;
            })
            .catch(() => {
                this.areCitiesReady = false;
                this.element.dataset.locationMapCitiesState = "error";
                this.scheduleGeoJsonRetry();
                return false;
            });
    }

    scheduleGeoJsonRetry() {
        if (!this.regionsUrl || this.geoJsonRetryTimer !== null) {
            return;
        }

        this.geoJsonRetryTimer = window.setTimeout(() => {
            this.geoJsonRetryTimer = null;
            this.loadGeoJsonMap();
        }, MAP_JSON_BACKGROUND_RETRY_DELAY);
    }

    clearGeoJsonRetry() {
        if (this.geoJsonRetryTimer === null) {
            return;
        }

        window.clearTimeout(this.geoJsonRetryTimer);
        this.geoJsonRetryTimer = null;
    }

    observeGeoJsonMapLoad() {
        if (!("IntersectionObserver" in window)) {
            this.loadGeoJsonMap();
            return;
        }

        this.visibilityObserver = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) {
                    return;
                }

                this.visibilityObserver?.disconnect();
                this.visibilityObserver = null;
                this.loadGeoJsonMap();
            },
            {
                rootMargin: "400px 0px",
            },
        );
        this.visibilityObserver.observe(this.element);

        if (this.isElementNearViewport()) {
            this.visibilityObserver.disconnect();
            this.visibilityObserver = null;
            this.loadGeoJsonMap();
        }
    }

    isElementNearViewport() {
        const rect = this.element.getBoundingClientRect();
        const preloadOffset = 400;

        return (
            rect.bottom >= -preloadOffset &&
            rect.top <= window.innerHeight + preloadOffset
        );
    }

    loadJsonWithRetry(url, attempt = 0) {
        if (!url) {
            return Promise.reject(new Error("Missing JSON URL"));
        }

        const retryDelay = MAP_JSON_RETRY_DELAYS[attempt] || 0;

        return this.wait(retryDelay)
            .then(() =>
                fetch(url, {
                    cache: "no-cache",
                    credentials: "same-origin",
                }),
            )
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`JSON request failed: ${response.status}`);
                }

                return response.json();
            })
            .catch((error) => {
                if (attempt + 1 < MAP_JSON_RETRY_DELAYS.length) {
                    return this.loadJsonWithRetry(url, attempt + 1);
                }

                throw error;
            });
    }

    wait(delay) {
        if (delay <= 0) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            window.setTimeout(resolve, delay);
        });
    }

    renderRegions(regions) {
        if (!regions?.features) {
            return false;
        }

        this.regionLayer.clearLayers();
        this.regionLayer.addData(this.simplifyRegionGeoJson(regions));
        this.disableRegionLayerSimplification();
        this.indexStateBounds();

        this.regionBounds = this.regionLayer.getBounds();
        if (this.regionBounds.isValid()) {
            this.updateMapMaxBounds();
        }
        this.isBaseMapReady = true;
        this.fitRegionBounds();
        this.fitActiveStateBounds();
        this.renderMarkers(this.filteredEntries, false);

        return true;
    }

    disableRegionLayerSimplification() {
        this.regionLayer.eachLayer((layer) => {
            if (!(layer instanceof L.Polyline)) {
                return;
            }

            // Leaflet's recursive simplifier can overflow on redraws of this GeoJSON.
            layer.options.smoothFactor = REGION_SMOOTH_FACTOR;
            layer._simplifyPoints = () => {};
        });
    }

    indexStateBounds() {
        this.stateBounds.clear();

        this.regionLayer.eachLayer((layer) => {
            const stateName = String(layer.feature?.properties?.GEN || "")
                .replace(/\s+\([^)]*\)$/, "")
                .trim();
            const layerBounds = layer.getBounds?.();
            if (!stateName || !layerBounds?.isValid()) {
                return;
            }

            const stateBounds = this.stateBounds.get(stateName);
            if (stateBounds) {
                stateBounds.extend(layerBounds);
                return;
            }

            this.stateBounds.set(
                stateName,
                L.latLngBounds(
                    layerBounds.getSouthWest(),
                    layerBounds.getNorthEast(),
                ),
            );
        });
    }

    simplifyRegionGeoJson(geoJson) {
        return {
            ...geoJson,
            features: geoJson.features.map((feature) => ({
                ...feature,
                geometry: this.simplifyRegionGeometry(feature.geometry),
            })),
        };
    }

    simplifyRegionGeometry(geometry) {
        if (!geometry?.coordinates) {
            return geometry;
        }

        if (geometry.type === "Polygon") {
            return {
                ...geometry,
                coordinates: this.simplifyPolygonCoordinates(
                    geometry.coordinates,
                ),
            };
        }

        if (geometry.type === "MultiPolygon") {
            return {
                ...geometry,
                coordinates: geometry.coordinates.map((polygon) =>
                    this.simplifyPolygonCoordinates(polygon),
                ),
            };
        }

        return geometry;
    }

    simplifyPolygonCoordinates(polygon) {
        if (!Array.isArray(polygon)) {
            return polygon;
        }

        return polygon.map((ring) => this.limitRingPoints(ring));
    }

    limitRingPoints(ring) {
        if (!Array.isArray(ring) || ring.length <= REGION_MAX_RING_POINTS) {
            return ring;
        }

        const isClosed = this.isSameCoordinate(ring[0], ring[ring.length - 1]);
        const sourceLength = isClosed ? ring.length - 1 : ring.length;
        const targetLength = isClosed
            ? REGION_MAX_RING_POINTS - 1
            : REGION_MAX_RING_POINTS;
        const step = Math.max(1, Math.ceil(sourceLength / targetLength));
        const limitedRing = [];

        for (let index = 0; index < sourceLength; index += step) {
            limitedRing.push(ring[index]);
        }

        if (
            !isClosed &&
            limitedRing[limitedRing.length - 1] !== ring[sourceLength - 1]
        ) {
            limitedRing.push(ring[sourceLength - 1]);
        }

        if (isClosed) {
            limitedRing.push(limitedRing[0]);
        }

        return limitedRing;
    }

    isSameCoordinate(firstCoordinate, secondCoordinate) {
        return (
            Array.isArray(firstCoordinate) &&
            Array.isArray(secondCoordinate) &&
            firstCoordinate[0] === secondCoordinate[0] &&
            firstCoordinate[1] === secondCoordinate[1]
        );
    }

    renderCities(cities) {
        if (!Array.isArray(cities)) {
            return;
        }

        this.cityLayer.clearLayers();
        cities.forEach((city) => {
            if (!this.isCapitalCity(city)) {
                return;
            }

            if (!Array.isArray(city.latLng) || city.latLng.length < 2) {
                return;
            }

            const lat = Number(city.latLng[0]);
            const lng = Number(city.latLng[1]);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                return;
            }

            const marker = L.marker([lat, lng], {
                icon: L.divIcon({
                    className: "location-map__city-anchor",
                    html: "",
                    iconAnchor: [0, 0],
                    iconSize: [0, 0],
                    tooltipAnchor: [0, 0],
                }),
                interactive: false,
                keyboard: false,
                opacity: 0,
            });

            if (city.name) {
                marker.bindTooltip(this.escapeHtml(city.name), {
                    className: "location-map__city-label",
                    direction: "top",
                    permanent: true,
                    offset: this.getCityLabelOffset(city.offset),
                });
            }

            this.cityLayer.addLayer(marker);
        });
    }

    getCityLabelOffset(offset) {
        if (!Array.isArray(offset) || offset.length < 2) {
            return [0, -4];
        }

        const xOffset = Number(offset[0]);
        const yOffset = Number(offset[1]);
        if (!Number.isFinite(xOffset) || !Number.isFinite(yOffset)) {
            return [0, -4];
        }

        return [xOffset, yOffset];
    }

    isCapitalCity(city) {
        return String(city.type || "").toLowerCase() === "capital";
    }

    bindEvents() {
        this.filter.addEventListener("change", () => {
            this.activeState = this.filter.value;
            this.applyFilter();
        });

        this.canvas.addEventListener(
            "click",
            (event) => this.handlePopupActionClick(event),
            true,
        );

        this.element.addEventListener("click", (event) => {
            const trigger = event.target.closest('[data-em-data="1"]');
            if (!trigger || !this.element.contains(trigger)) {
                return;
            }

            event.preventDefault();
            this.activateProtectedEmail(trigger);
        });

        this.switchButtons.forEach((button) => {
            button.addEventListener("click", () => {
                this.setActiveView(button.dataset.locationMapSwitch);
            });
        });
    }

    handlePopupActionClick(event) {
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target?.closest("[data-location-map-popup-entry]");
        if (!trigger || !this.canvas.contains(trigger)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.openEntry(trigger.dataset.locationMapPopupEntry, true);
    }

    applyFilter() {
        const filteredEntries = this.activeState
            ? this.entries.filter((entry) => entry.state === this.activeState)
            : this.entries;
        this.filteredEntries = filteredEntries;

        this.renderList(filteredEntries);
        this.renderMarkers(filteredEntries);
        this.updateCount(filteredEntries.length);
    }

    renderList(entries) {
        this.list.replaceChildren();

        entries.forEach((entry) => {
            const item = document.createElement("li");
            const details = document.createElement("details");
            details.className = "location-map__item";
            details.dataset.locationMapEntry = entry.id;

            const summary = document.createElement("summary");
            summary.className = "location-map__item-summary";

            const summaryContent = document.createElement("span");
            summaryContent.className = "location-map__item-summary-content";
            summaryContent.append(
                this.createTextElement(
                    "span",
                    "location-map__item-type",
                    entry.networkType,
                ),
                this.createTextElement(
                    "strong",
                    "location-map__item-title",
                    entry.name,
                ),
                this.createTextElement(
                    "span",
                    "location-map__item-address",
                    entry.address,
                ),
            );
            summary.append(summaryContent, this.createToggleIcon());

            summary.addEventListener("click", () => {
                const wasOpen = details.open;
                window.requestAnimationFrame(() => {
                    if (wasOpen) {
                        this.deactivateEntry(entry.id);
                        return;
                    }

                    this.focusEntry(entry);
                });
            });

            const profile = document.createElement("div");
            profile.className = "location-map__item-profile";
            this.appendOptionalBlock(profile, "Kurzprofil", entry.profile);
            this.appendContactBlock(profile, entry);

            details.append(summary, profile);
            item.append(details);
            this.list.append(item);
        });

        this.empty.hidden = entries.length > 0;
    }

    renderMarkers(entries, fitToMarkers = true) {
        this.clusterLayer.clearLayers();

        if (!this.isBaseMapReady) {
            window.requestAnimationFrame(() => {
                this.map.invalidateSize({ pan: false });
                this.scheduleMarkerTabOrderUpdate();
            });
            return;
        }

        const visibleMarkers = [];

        entries.forEach((entry) => {
            const marker = this.markers.get(entry.id);
            if (marker) {
                visibleMarkers.push(marker);
                this.clusterLayer.addLayer(marker);
            }
        });

        if (fitToMarkers) {
            const hasActiveStateBounds = this.fitActiveStateBounds();
            if (!hasActiveStateBounds && visibleMarkers.length > 0) {
                const group = L.featureGroup(visibleMarkers);
                this.map.fitBounds(group.getBounds(), {
                    padding: [32, 32],
                    maxZoom: 12,
                });
            } else if (!hasActiveStateBounds) {
                this.fitRegionBounds();
            }
        }

        window.requestAnimationFrame(() => {
            this.map.invalidateSize({ pan: false });
            this.scheduleMarkerTabOrderUpdate();
        });
    }

    createMarker(entry) {
        const markerColor = this.getMarkerColor(entry.networkType);
        const marker = L.marker([entry.latitude, entry.longitude], {
            icon: L.divIcon({
                className: "location-map__marker",
                html: `<span class="location-map__marker-dot" style="background-color: ${markerColor}"></span>`,
                iconSize: [28, 28],
                iconAnchor: [14, 14],
                popupAnchor: [0, -14],
            }),
        });

        marker.bindPopup(this.createPopupMarkup(entry), {
            autoPan: false,
            keepInView: false,
            maxWidth: 320,
            minWidth: 220,
        });
        marker.on("popupopen", (event) => {
            this.activatePopupBounds();

            const popupElement = event.popup?.getElement();
            if (!popupElement) {
                return;
            }

            L.DomEvent.disableClickPropagation(popupElement);
            L.DomEvent.disableScrollPropagation(popupElement);
        });
        marker.on("popupclose", () => this.deactivatePopupBounds());
        marker.on("click", () => {
            this.openEntry(entry.id, false, true);
            this.panToMarker(marker, () => this.updatePopupPosition(marker));
        });
        this.markers.set(entry.id, marker);
    }

    getMarkerColor(networkType) {
        const normalizedType = this.normalizeNetworkType(networkType);
        const configuredColor = MARKER_TYPE_COLORS.get(normalizedType);
        if (configuredColor) {
            return configuredColor;
        }

        return MARKER_FALLBACK_COLORS[
            this.getStableIndex(normalizedType, MARKER_FALLBACK_COLORS.length)
        ];
    }

    normalizeNetworkType(networkType) {
        return String(networkType || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/ß/g, "ss")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "");
    }

    getStableIndex(value, length) {
        let hash = 0;
        for (let index = 0; index < value.length; index += 1) {
            hash = (hash * 31 + value.charCodeAt(index)) % length;
        }

        return hash;
    }

    focusEntry(entry) {
        const marker = this.markers.get(entry.id);
        if (!marker) {
            return;
        }

        this.openEntry(entry.id, false, true);
        this.openMarkerPopup(entry.id);
    }

    openEntry(id, switchToList = false, scrollToEntry = switchToList) {
        if (switchToList && window.matchMedia(MOBILE_QUERY).matches) {
            this.setActiveView("list");
        }

        const activeEntry = this.highlightEntry(id);
        if (activeEntry && scrollToEntry) {
            this.scrollEntryIntoList(activeEntry);
        }
    }

    highlightEntry(id) {
        let activeEntry = null;

        this.element
            .querySelectorAll("[data-location-map-entry]")
            .forEach((entryElement) => {
                const isActive = entryElement.dataset.locationMapEntry === id;
                entryElement.classList.toggle("is-active", isActive);
                entryElement.open = isActive;

                if (isActive) {
                    activeEntry = entryElement;
                }
            });

        return activeEntry;
    }

    scrollEntryIntoList(entryElement) {
        this.afterNextPaint(() => {
            const listRect = this.list.getBoundingClientRect();
            const entryRect = entryElement.getBoundingClientRect();
            const targetScrollTop =
                this.list.scrollTop + entryRect.top - listRect.top;

            this.list.scrollTo({
                top: Math.max(targetScrollTop, 0),
                behavior: "smooth",
            });
        });
    }

    deactivateEntry(id) {
        this.popupRequestId += 1;

        if (this.pendingPopupEntryId === id) {
            this.pendingPopupEntryId = null;
        }

        this.element
            .querySelectorAll("[data-location-map-entry]")
            .forEach((entryElement) => {
                if (entryElement.dataset.locationMapEntry !== id) {
                    return;
                }

                entryElement.classList.remove("is-active");
                entryElement.open = false;
            });

        const marker = this.markers.get(id);
        if (marker) {
            marker.closePopup();
        }
    }

    setActiveView(view) {
        if (!["list", "map"].includes(view)) {
            return;
        }

        this.body.dataset.locationMapView = view;
        this.switchButtons.forEach((button) => {
            button.setAttribute(
                "aria-pressed",
                String(button.dataset.locationMapSwitch === view),
            );
        });

        if (view === "map" || !window.matchMedia(MOBILE_QUERY).matches) {
            window.requestAnimationFrame(() => {
                this.loadGeoJsonMap();
                this.map.invalidateSize({ pan: false });
                if (!this.fitActiveStateBounds()) {
                    this.fitRegionBounds();
                }
                this.scheduleMarkerTabOrderUpdate();
                if (this.pendingPopupEntryId) {
                    const pendingPopupEntryId = this.pendingPopupEntryId;
                    this.pendingPopupEntryId = null;
                    this.openMarkerPopup(pendingPopupEntryId);
                }
            });
        }
    }

    fitRegionBounds() {
        if (!this.regionBounds?.isValid() || !this.isMapVisible()) {
            return;
        }

        this.updateMapMaxBounds();
        this.map.fitBounds(this.regionBounds, {
            animate: false,
            padding: [24, 24],
        });
        this.regionMinZoom = this.map.getZoom();
        this.map.setMinZoom(this.regionMinZoom);
        this.snapMapToRegionBounds(false);
    }

    fitActiveStateBounds() {
        if (!this.activeState) {
            return false;
        }

        const bounds = this.stateBounds.get(this.activeState);
        if (!bounds?.isValid()) {
            return false;
        }

        if (this.isMapVisible()) {
            this.updateMapMaxBounds();
            this.map.fitBounds(bounds, {
                animate: false,
                padding: [32, 32],
            });
            this.snapMapToRegionBounds(false);
        }

        return true;
    }

    snapMapToRegionBounds(animate = true) {
        if (!this.regionBounds?.isValid() || !this.isMapVisible()) {
            return;
        }

        const options = animate ? SNAP_OPTIONS : { animate: false };
        if (
            this.regionMinZoom !== null &&
            this.map.getZoom() <= this.regionMinZoom + MIN_ZOOM_OFFSET
        ) {
            this.map.panTo(this.regionBounds.getCenter(), options);
            return;
        }

        this.map.panInsideBounds(this.getActivePanBounds(), options);
    }

    updateMapMaxBounds() {
        if (!this.regionBounds?.isValid()) {
            return;
        }

        this.map.setMaxBounds(this.getMaxBounds());
    }

    getActivePanBounds() {
        if (this.isPopupBoundsActive) {
            return this.getMaxBounds();
        }

        return this.regionBounds;
    }

    getMaxBounds() {
        return this.regionBounds.pad(this.getMaxBoundsPadding());
    }

    getMaxBoundsPadding() {
        const isMobile = window.matchMedia(MOBILE_QUERY).matches;
        if (this.isPopupBoundsActive) {
            return isMobile
                ? MOBILE_POPUP_MAX_BOUNDS_PADDING
                : POPUP_MAX_BOUNDS_PADDING;
        }

        return isMobile ? MOBILE_MAX_BOUNDS_PADDING : MAX_BOUNDS_PADDING;
    }

    activatePopupBounds() {
        if (this.isPopupBoundsActive) {
            return;
        }

        this.isPopupBoundsActive = true;
        this.updateMapMaxBounds();
    }

    deactivatePopupBounds() {
        if (!this.isPopupBoundsActive) {
            return;
        }

        this.isPopupBoundsActive = false;
        this.updateMapMaxBounds();
        this.snapMapToRegionBounds(false);
    }

    scheduleMarkerTabOrderUpdate() {
        window.cancelAnimationFrame(this.markerTabOrderFrame);
        this.markerTabOrderFrame = window.requestAnimationFrame(() => {
            this.markerTabOrderFrame = null;
            this.afterNextPaint(() => this.updateMarkerTabOrder());
        });
    }

    updateMarkerTabOrder() {
        const markerPane = this.map?.getPane("markerPane");
        if (!markerPane) {
            return;
        }

        const markerElements = [
            ...markerPane.querySelectorAll(
                ".location-map__cluster, .location-map__marker",
            ),
        ].filter((element) => this.isVisibleMarkerElement(element));

        markerElements
            .sort((firstElement, secondElement) => {
                const firstRect = firstElement.getBoundingClientRect();
                const secondRect = secondElement.getBoundingClientRect();
                const firstY = firstRect.top + firstRect.height / 2;
                const secondY = secondRect.top + secondRect.height / 2;
                const firstX = firstRect.left + firstRect.width / 2;
                const secondX = secondRect.left + secondRect.width / 2;
                const yDifference = firstY - secondY;

                return Math.abs(yDifference) > 1
                    ? yDifference
                    : firstX - secondX;
            })
            .forEach((element) => {
                element.tabIndex = 0;
                markerPane.append(element);
            });
    }

    isVisibleMarkerElement(element) {
        if (!(element instanceof HTMLElement)) {
            return false;
        }

        const rect = element.getBoundingClientRect();

        return rect.width > 0 && rect.height > 0 && element.offsetParent !== null;
    }

    openMarkerPopup(id) {
        const marker = this.markers.get(id);
        if (!marker) {
            return;
        }

        const requestId = (this.popupRequestId += 1);

        if (!this.isMapVisible()) {
            this.pendingPopupEntryId = id;
            return;
        }

        if (!this.clusterLayer.hasLayer(marker)) {
            return;
        }

        this.pendingPopupEntryId = null;
        this.map.closePopup();
        this.activatePopupBounds();
        this.map.invalidateSize({ pan: false });

        const showPopup = () => {
            if (requestId !== this.popupRequestId) {
                return;
            }

            this.panToMarker(marker, () => {
                if (requestId !== this.popupRequestId) {
                    return;
                }

                marker.openPopup();
                this.updatePopupPosition(marker);
            });
        };

        this.clusterLayer.zoomToShowLayer(marker, showPopup);
    }

    panToMarker(marker, callback) {
        const target = marker.getLatLng();

        if (this.map.getCenter().distanceTo(target) < 1) {
            this.afterNextPaint(callback);
            return;
        }

        let isSettled = false;
        const settle = () => {
            if (isSettled) {
                return;
            }

            isSettled = true;
            this.map.off("moveend", settle);
            this.afterNextPaint(callback);
        };

        this.map.once("moveend", settle);
        this.map.panTo(target, { animate: false });
        window.setTimeout(settle, 50);
    }

    updatePopupPosition(marker) {
        const popup = marker.getPopup();
        if (!popup) {
            return;
        }

        popup.update();

        let updateTimeout = null;
        const updateAfterMove = () => {
            window.clearTimeout(updateTimeout);
            popup.update();
        };

        this.map.once("moveend", updateAfterMove);
        updateTimeout = window.setTimeout(() => {
            this.map.off("moveend", updateAfterMove);
            popup.update();
        }, 300);

        this.afterNextPaint(() => popup.update());
    }

    afterNextPaint(callback) {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(callback);
        });
    }

    isMapVisible() {
        return this.canvas.offsetParent !== null && this.canvas.offsetWidth > 0;
    }

    updateCount(count) {
        if (!this.count) {
            return;
        }

        this.count.textContent =
            count === 1 ? "1 Eintrag" : `${count} Einträge`;
    }

    appendOptionalBlock(parent, headline, text) {
        if (!text) {
            return;
        }

        parent.append(
            this.createTextElement(
                "h3",
                "location-map__item-subtitle",
                headline,
            ),
            this.createTextElement("p", "", text),
        );
    }

    appendContactBlock(parent, entry) {
        const contactItems = [];

        if (entry.phone) {
            contactItems.push(
                this.createLink(`tel:${entry.phone}`, entry.phone),
            );
        }

        const contactLink = this.createContactLink(entry.contact);
        if (contactLink) {
            contactItems.push(contactLink);
        }

        if (entry.website) {
            contactItems.push(
                this.createLink(entry.website, entry.website, true),
            );
        }

        if (contactItems.length === 0) {
            return;
        }

        parent.append(
            this.createTextElement(
                "h3",
                "location-map__item-subtitle",
                "Kontakt",
            ),
        );
        const list = document.createElement("ul");
        list.className = "location-map__contact-list";
        contactItems.forEach((contactItem) => {
            const item = document.createElement("li");
            item.append(contactItem);
            list.append(item);
        });
        parent.append(list);
    }

    createLink(href, text, external = false) {
        const link = document.createElement("a");
        link.href = href;
        link.textContent = text;

        if (external) {
            link.target = "_blank";
            link.rel = "noreferrer";
        }

        return link;
    }

    createContactLink(contact) {
        if (!contact || typeof contact !== "object") {
            return null;
        }

        if (contact.type === "email" && contact.value) {
            return this.createProtectedEmail(contact.value);
        }

        if (contact.type === "url" && contact.href) {
            const link = this.createLink(
                contact.href,
                contact.value || contact.href,
                true,
            );
            link.classList.add("no-external");
            link.dataset.mailtoToken = "1";

            return link;
        }

        return null;
    }

    createProtectedEmail(email) {
        const emailParts = this.splitEmail(email);
        if (!emailParts) {
            return this.createTextElement("span", "", email);
        }

        const link = document.createElement("a");
        link.href = "#";
        link.className = "location-map__protected-email";
        link.dataset.emData = "1";
        link.dataset.em1 = emailParts.localPart;
        link.dataset.em2 = emailParts.domain;
        link.dataset.emClass = link.className;
        link.dataset.mailtoToken = "1";
        link.textContent = `${emailParts.localPart}${EMAIL_OBFUSCATION_LABEL}${emailParts.domain}`;

        return link;
    }

    activateProtectedEmail(trigger) {
        const localPart = trigger.dataset.em1 || "";
        const domain = trigger.dataset.em2 || "";
        const email = `${localPart}@${domain}`;
        if (!this.splitEmail(email)) {
            return;
        }

        const label =
            trigger.dataset.emName ||
            `${localPart}${EMAIL_OBFUSCATION_LABEL}${domain}`;
        const link = this.createLink(`mailto:${email}`, label);
        const className = trigger.dataset.emClass;
        if (className) {
            link.className = className;
        }
        link.target = "_blank";

        trigger.replaceWith(link);
        link.focus();
        link.click();
    }

    splitEmail(email) {
        const normalizedEmail = String(email || "").trim();
        const parts = normalizedEmail.split("@");
        if (parts.length !== 2 || !parts[0] || !parts[1]) {
            return null;
        }

        return {
            localPart: parts[0],
            domain: parts[1],
        };
    }

    createTextElement(tagName, className, text) {
        const element = document.createElement(tagName);
        if (className) {
            element.className = className;
        }
        element.textContent = text || "";

        return element;
    }

    createToggleIcon() {
        const namespace = "http://www.w3.org/2000/svg";
        const icon = document.createElementNS(namespace, "svg");
        icon.setAttribute("xmlns", namespace);
        icon.setAttribute("width", "24");
        icon.setAttribute("height", "24");
        icon.setAttribute("viewBox", "0 0 24 24");
        icon.setAttribute("fill", "none");
        icon.setAttribute("aria-hidden", "true");
        icon.setAttribute("focusable", "false");
        icon.classList.add("location-map__item-toggle-icon");

        const circle = document.createElementNS(namespace, "rect");
        circle.setAttribute("width", "24");
        circle.setAttribute("height", "24");
        circle.setAttribute("rx", "12");
        circle.setAttribute("fill", "#56D48D");

        const arrow = document.createElementNS(namespace, "path");
        arrow.setAttribute(
            "d",
            "M12.0707 13.3139L17.0207 8.36388C17.1129 8.26837 17.2232 8.19219 17.3453 8.13978C17.4673 8.08737 17.5985 8.05979 17.7313 8.05863C17.864 8.05748 17.9957 8.08278 18.1186 8.13306C18.2415 8.18334 18.3532 8.25759 18.4471 8.35149C18.5409 8.44538 18.6152 8.55703 18.6655 8.67993C18.7158 8.80282 18.7411 8.9345 18.7399 9.06728C18.7388 9.20006 18.7112 9.33128 18.6588 9.45329C18.6063 9.57529 18.5302 9.68564 18.4347 9.77788L12.7777 15.4349C12.5901 15.6224 12.3358 15.7277 12.0707 15.7277C11.8055 15.7277 11.5512 15.6224 11.3637 15.4349L5.70666 9.77788C5.61115 9.68564 5.53496 9.57529 5.48255 9.45329C5.43014 9.33128 5.40256 9.20006 5.4014 9.06728C5.40025 8.9345 5.42555 8.80282 5.47583 8.67993C5.52611 8.55703 5.60037 8.44538 5.69426 8.35149C5.78815 8.25759 5.89981 8.18334 6.0227 8.13306C6.1456 8.08278 6.27728 8.05748 6.41006 8.05863C6.54284 8.05979 6.67406 8.08737 6.79606 8.13978C6.91806 8.19219 7.02841 8.26837 7.12066 8.36388L12.0707 13.3139Z",
        );
        arrow.setAttribute("fill", "#0B407D");

        icon.append(circle, arrow);

        return icon;
    }

    createPopupMarkup(entry) {
        const address = entry.small_address
            ? `<p>${this.escapeHtml(entry.small_address)}</p>`
            : "";
        const action = `<button type="button" class="location-map__popup-action" data-location-map-popup-entry="${this.escapeAttribute(entry.id)}">mehr erfahren</button>`;

        return `
            <div class="location-map__popup">
                <strong>${this.escapeHtml(entry.name)}</strong>
                ${address}
                ${action}
            </div>
        `;
    }

    hasCoordinates(entry) {
        return (
            Number.isFinite(entry.latitude) && Number.isFinite(entry.longitude)
        );
    }

    escapeHtml(value) {
        return String(value || "")
            .replaceAll("&", "&amp;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;")
            .replaceAll('"', "&quot;")
            .replaceAll("'", "&#039;");
    }

    escapeAttribute(value) {
        return this.escapeHtml(value).replaceAll("`", "&#096;");
    }
}

export function initLocationMaps() {
    document.querySelectorAll("[data-location-map]").forEach((element) => {
        if (element.dataset.locationMapInitialized === "true") {
            return;
        }

        element.dataset.locationMapInitialized = "true";
        new LocationMap(element);
    });
}
